import { Component, createSignal, onMount, createMemo } from 'solid-js';
import DataTable, { Column } from '../components/tables/DataTable';
import TableSkeleton from '../components/ui/TableSkeleton';
import SearchInput from '../components/ui/SearchInput';
import PlatformIcon, { getPlatformColor } from '../components/ui/PlatformIcon';
import CountryFlag from '../components/ui/CountryFlag';
import NetworkLogo from '../components/ui/NetworkLogo';
import { visitsApi } from '../services/api';
import { useSocketEvent } from '../services/socket';
import { setDocumentTitle } from '../utils/document';
import type { Visit } from '../types';
import moment from 'moment';
import './NewRowAnimation.css';

interface VisitWithAnimation extends Visit {
  _isNew?: boolean;
}

const Clicks: Component = () => {
  const [visits, setVisits] = createSignal<VisitWithAnimation[]>([]);
  const [loading, setLoading] = createSignal(true);
  const [searchInput, setSearchInput] = createSignal('');
  const [realtimeCount, setRealtimeCount] = createSignal(0);

  // Check if a row has meaningful data (not completely empty)
  const hasValidData = (visit: Visit): boolean => {
    if (!visit) return false;
    return !!(
      visit.user ||
      visit.country ||
      visit.region ||
      visit.city ||
      visit.ip_address ||
      visit.traffic_source
    );
  };

  // Filtered visits based on search input and valid data (supports comma-separated OR filter)
  const filteredVisits = createMemo(() => {
    const search = searchInput().toLowerCase().trim();

    // First filter out completely empty rows
    const validVisits = visits().filter(hasValidData);

    if (!search) return validVisits;

    // Split by comma and filter empty terms - supports "term1,term2,term3" format
    const searchTerms = search.split(',').map(term => term.trim()).filter(Boolean);
    if (searchTerms.length === 0) return validVisits;

    // Filter: each row must match at least ONE search term (OR logic)
    return validVisits.filter(visit => {
      if (!visit) return false;
      const searchFields = [
        visit.user?.toLowerCase(),
        visit.country?.toLowerCase(),
        visit.region?.toLowerCase(),
        visit.city?.toLowerCase(),
        visit.ip_address?.toLowerCase(),
        visit.traffic_source?.toLowerCase(),
      ];
      // Each row must match at least ONE term (OR between terms)
      return searchTerms.some(term =>
        searchFields.some(field => field?.includes(term))
      );
    });
  });

  // Get icon based on traffic source (platform)
  const getTrafficSourceIcon = (visit: Visit) => {
    return (
      <PlatformIcon
        platform={visit.traffic_source}
        size={16}
        class={`${getPlatformColor(visit.traffic_source)}`}
      />
    );
  };

  // Apply animation and highlight to new rows
  const getRowClass = (visit: VisitWithAnimation): string => {
    if (!visit) return '';
    if (visit._isNew) {
      return 'new-row-animate new-row-highlight';
    }
    return '';
  };


  const columns: Column<Visit>[] = [
    {
      key: 'user',
      label: 'User',
      sortable: true,
      render: (visit) => (
        visit?.user ? <span class="font-mono text-xs">{visit.user}</span> : null
      ),
    },
    {
      key: 'traffic_source',
      label: 'Platform',
      sortable: false,
      render: (visit) => visit ? <div class="flex justify-center">{getTrafficSourceIcon(visit)}</div> : null,
    },
    {
      key: 'network',
      label: 'Network',
      sortable: false,
      render: (visit) => (
        visit ? <div class="flex justify-center"><NetworkLogo network={visit.network} size={16} /></div> : null
      ),
    },
    {
      key: 'country',
      label: 'Location',
      sortable: true,
      render: (visit) => (
        visit && visit.country ? (
          <div class="flex items-center gap-3">
            <div class="flex items-center gap-1 flex-shrink-0">
              <CountryFlag code={visit.country} size={16} />
              <span class="text-xs font-medium">{visit.country}</span>
            </div>
            <div class="flex flex-col gap-0.5">
              <span class="text-2xs text-gray-600 dark:text-gray-400">{visit.region}</span>
              <span class="text-2xs font-medium">{visit.city}</span>
            </div>
          </div>
        ) : null
      ),
    },
    {
      key: 'ip_address',
      label: 'IP Address',
      sortable: false,
      render: (visit) => (
        visit?.ip_address ? <span class="font-mono text-xs text-gray-600 dark:text-gray-400">{visit.ip_address}</span> : null
      ),
    },
    {
      key: 'created_at',
      label: 'Time',
      sortable: true,
      render: (visit) => (
        visit?.created_at ? <span class="text-xs text-gray-600 dark:text-gray-400">{moment(visit.created_at).fromNow()}</span> : null
      ),
    },
  ];

  const loadVisits = async () => {
    setLoading(true);
    try {
      const data = await visitsApi.getAll({
        page: 1,
        limit: 300,
      });
      // Filter out blank rows - only keep visits with valid data
      const validVisits = (data.data || []).filter(hasValidData);
      setVisits(validVisits);
    } catch (error) {
      setVisits([]);
    } finally {
      setLoading(false);
    }
  };

  onMount(() => {
    // Set page title
    setDocumentTitle('Clicks');

    // Load initial clicks
    loadVisits();

    // Handle new click
    const handleNewClick = (data: Visit) => {
      // Add new click to the beginning of the list
      setVisits((prevVisits) => {
        const newVisit: VisitWithAnimation = {
          ...data,
          created_at: data.created_at || new Date().toISOString(),
          _isNew: true,
        };

        // Increment realtime counter
        setRealtimeCount((c) => c + 1);

        // Keep only recent 300 items in memory
        const updated = [newVisit, ...prevVisits].slice(0, 300);

        // Remove _isNew flag after animation completes (0.4s for slide-in + 2s for highlight = 2.4s)
        setTimeout(() => {
          setVisits((current) =>
            current && current.length > 0
              ? current.map((visit) =>
                  visit && visit._isNew ? { ...visit, _isNew: false } : visit
                )
              : current
          );
        }, 2400);

        return updated;
      });
    };

    // Set up real-time socket listener for new clicks
    useSocketEvent<Visit>('click-recorded', handleNewClick);

    // Also listen for new-click events as backup
    useSocketEvent<Visit>('new-click', handleNewClick);
  });

  return (
    <div class="space-y-6">
      {/* Header */}
      <div>
        <h1 class="text-2xl font-bold text-gray-900 dark:text-white">
          Latest Clicks
        </h1>
      </div>

      {/* Data Table Wrapper */}
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow w-full">
        <SearchInput
          value={searchInput()}
          onInput={(e) => setSearchInput(e)}
          placeholder="Search by User, Country, City, Platform, or IP..."
        />

        <div class="overflow-x-auto">
          {loading() ? (
            <TableSkeleton rows={5} columns={columns.length} />
          ) : (
            <DataTable
              data={filteredVisits()}
              columns={columns}
              loading={loading()}
              searchable={false}
              defaultSortKey="created_at"
              defaultSortOrder="desc"
              getRowClass={getRowClass}
              rowPadding="py-2"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default Clicks;
